// Annual charter renewal progress for the dashboard's Charter Renewal widget. Pure functions over units' recharter data;
// nothing here reads or writes Firestore, and nothing here changes how expirations, warnings or renewal Actions work.
//
// A unit has renewed for a charter year when its effective charter expiration reaches that year. The effective
// expiration is the report's own expiry unless the report's "next charter expiry" holds a later date (the import already
// stores that date as `charterExpires`; it is re-derived here so units imported either way read the same).

import type { NamedRecord } from '../types.ts';

export type ISODate = string;
export type RenewalState = 'renewed' | 'pending' | 'overdue';

/** The renewal cycle opens this many days before its year-end, matching the first (90-day) milestone of the renewal
 *  automation (functions/src/rechartAutomation.ts), which is when a unit's renewal starts being tracked. */
export const RENEWAL_CYCLE_OPENS_DAYS_BEFORE_YEAR_END = 90;

const ISO_DATE = /^\d{4}-\d{2}-\d{2}$/;

function addDays(iso: ISODate, days: number): ISODate {
  const [y, m, d] = iso.split('-').map(Number);
  return new Date(Date.UTC(y, m - 1, d + days)).toISOString().slice(0, 10);
}

/** Today as a local calendar date, YYYY-MM-DD. */
export function localTodayIso(now: Date = new Date()): ISODate {
  const pad = (n: number) => String(n).padStart(2, '0');
  return `${now.getFullYear()}-${pad(now.getMonth() + 1)}-${pad(now.getDate())}`;
}

/** The charter year being renewed. Charters run to December 31, and a cycle opens 90 days before that year-end (Oct 2),
 *  so Oct 2 2026 through Oct 1 2027 is the 2027 cycle. It does not advance on January 1: through the following
 *  autumn the widget keeps showing the cycle that just ended, so renewals still unresolved after year-end stay
 *  visible (as overdue) until the next cycle opens. */
export function renewalCycleYear(today: ISODate): number {
  const year = Number(today.slice(0, 4));
  const opens = addDays(`${year}-12-31`, -RENEWAL_CYCLE_OPENS_DAYS_BEFORE_YEAR_END);
  return today >= opens ? year + 1 : year;
}

export interface RechartLike {
  charterExpires?: string | null;
  nextCharterExpires?: string | null;
}

/** The unit's confirmed effective charter expiration, or null when it has no charter date at all. */
export function effectiveCharterExpiry(r: RechartLike | null | undefined): ISODate | null {
  const current = r?.charterExpires && ISO_DATE.test(r.charterExpires) ? r.charterExpires : null;
  const next = r?.nextCharterExpires && ISO_DATE.test(r.nextCharterExpires) ? r.nextCharterExpires : null;
  if (next && (!current || next > current)) return next;
  return current;
}

export function renewalState(expiry: ISODate, targetYear: number, today: ISODate): RenewalState {
  if (Number(expiry.slice(0, 4)) >= targetYear) return 'renewed';
  return expiry < today ? 'overdue' : 'pending';
}

export interface RenewalRow {
  districtId: string;
  name: string;
  renewed: number;
  pending: number;
  overdue: number;
  total: number;
  /** Whole percent renewed; 100 only when every unit has renewed, 0 only when none has. */
  pct: number;
}

export interface RenewalProgress {
  year: number;
  rows: RenewalRow[];
  totals: RenewalRow;
}

function renewedPct(renewed: number, total: number): number {
  if (!total || !renewed) return 0;
  if (renewed === total) return 100;
  return Math.min(99, Math.max(1, Math.round((renewed / total) * 100)));
}

function makeRow(districtId: string, name: string, renewed: number, pending: number, overdue: number): RenewalRow {
  const total = renewed + pending + overdue;
  return { districtId, name, renewed, pending, overdue, total, pct: renewedPct(renewed, total) };
}

/** Renewal progress per district for the cycle that applies on `today`. Every unit with a charter date is in exactly
 *  one bucket, so renewed + pending + overdue is the district's renewal population (the same units the dashboard's
 *  district summaries count). Districts with no such unit are left out. */
export function renewalProgress(
  units: { districtId: string; recharter?: RechartLike | null }[],
  districts: NamedRecord[],
  today: ISODate,
): RenewalProgress {
  const year = renewalCycleYear(today);
  const counts = new Map<string, { renewed: number; pending: number; overdue: number }>();
  for (const u of units) {
    const expiry = effectiveCharterExpiry(u.recharter);
    if (!expiry) continue;
    const c = counts.get(u.districtId) ?? { renewed: 0, pending: 0, overdue: 0 };
    c[renewalState(expiry, year, today)]++;
    counts.set(u.districtId, c);
  }
  const rows: RenewalRow[] = [];
  for (const d of districts) {
    const c = counts.get(d.id);
    if (c) rows.push(makeRow(d.id, d.name, c.renewed, c.pending, c.overdue));
  }
  const sum = (k: 'renewed' | 'pending' | 'overdue') => rows.reduce((n, r) => n + r[k], 0);
  return { year, rows, totals: makeRow('', 'Council', sum('renewed'), sum('pending'), sum('overdue')) };
}
